// Turns a job description as LinkedIn gives it (plain text with a blank line after every line)
// into blocks a page can lay out properly: headings, paragraphs and bullet lists.
//
//   [{ type: 'heading', text }, { type: 'paragraph', text }, { type: 'list', items: [text] }]

const BULLET = /^([•●▪◦∙·*\-–—]|\d{1,2}[.)])\s+/
const SENTENCE_END = /[.!?:;,]$/
const MAX_HEADING = 60
const MAX_TITLE_LINE = 40
// Small words that may stay lower case in a heading written in title case ("Roles and Responsibilities").
const CONNECTORS = new Set(['and', 'or', 'of', 'the', 'for', 'to', 'in', 'with', 'a', 'an', 'on', 'at', 'you', 'your', '&'])

// "Role Responsibilities", "What We Offer": a few words, each starting with a capital.
function isTitleCase(line) {
  const words = line.split(' ')
  return words.length <= 6 && words.every((word) => /^[A-Z0-9]/.test(word) || CONNECTORS.has(word.toLowerCase()))
}

// A short line is a heading when it ends in a colon, or is written in capitals, or has no
// sentence punctuation and is followed straight away by a bullet list, or is a few words in title
// case with a clearly longer line after it (a heading over a paragraph).
function isHeading(line, next) {
  if (line.length > MAX_HEADING) return false
  if (line.endsWith(':')) return true
  if (/[A-Z]/.test(line) && line === line.toUpperCase() && !/[a-z]/.test(line)) return true
  if (next && BULLET.test(next) && !SENTENCE_END.test(line)) return true

  return Boolean(next && line.length <= MAX_TITLE_LINE && !/[.!?:;,]/.test(line) && isTitleCase(line) && next.length > line.length * 1.5)
}

export function parseDescription(text) {
  const lines = String(text || '')
    .replace(/\r/g, '')
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)

  // The page already has an "About the job" heading, so LinkedIn's own copy of it is dropped.
  if (lines.length && /^about the job:?$/i.test(lines[0])) lines.shift()

  const blocks = []
  lines.forEach((line, index) => {
    const bullet = BULLET.exec(line)
    if (bullet) {
      const item = line.slice(bullet[0].length).trim()
      const last = blocks[blocks.length - 1]
      if (last?.type === 'list') last.items.push(item)
      else blocks.push({ type: 'list', items: [item] })
      return
    }

    if (isHeading(line, lines[index + 1])) {
      blocks.push({ type: 'heading', text: line.replace(/:$/, '') })
      return
    }

    blocks.push({ type: 'paragraph', text: line })
  })
  return blocks
}
